import api from './api';

export type CommChannel = 'EMAIL' | 'SMS';
export type CommDirection = 'INCOMING' | 'OUTGOING';

export interface Communication {
  id: number;
  patient_id: number | null;
  tenant_id: number | null;
  channel: CommChannel;
  direction: CommDirection;
  status: string;
  source: string;
  subject: string | null;
  body_text: string | null;
  body_html: string | null;
  from_address: string | null;
  to_address: string | null;
  created_by: number | null;
  created_at: string;
  received_at: string | null;
}

export interface CommunicationListResponse {
  items: Communication[];
  total: number;
  page: number;
  page_size: number;
  total_pages: number;
}

export interface CommunicationFilters {
  channel?: CommChannel;
  direction?: CommDirection;
  page?: number;
  page_size?: number;
}

export const communicationService = {
  async getPatientCommunications(
    patientId: number,
    filters: CommunicationFilters = {},
  ): Promise<CommunicationListResponse> {
    const params = new URLSearchParams();
    if (filters.channel) params.append('channel', filters.channel);
    if (filters.direction) params.append('direction', filters.direction);
    if (filters.page) params.append('page', String(filters.page));
    if (filters.page_size) params.append('page_size', String(filters.page_size));
    const response = await api.get(
      `/patients/${patientId}/communications?${params.toString()}`,
    );
    return response.data;
  },

  async sendEmail(
    patientId: number,
    data: { subject: string; body_html: string },
  ): Promise<Communication> {
    const response = await api.post(
      `/patients/${patientId}/communications/email`,
      data,
    );
    return response.data;
  },

  /** A lead's timeline. Same shape as the patient one, so the same panel
   *  renders it unchanged. */
  async getLeadCommunications(
    leadId: number,
    filters: CommunicationFilters = {},
  ): Promise<CommunicationListResponse> {
    const params = new URLSearchParams();
    if (filters.channel) params.append('channel', filters.channel);
    if (filters.direction) params.append('direction', filters.direction);
    if (filters.page) params.append('page', String(filters.page));
    if (filters.page_size) params.append('page_size', String(filters.page_size));
    const response = await api.get(
      `/leads/${leadId}/communications?${params.toString()}`,
    );
    return response.data;
  },

  async sendLeadEmail(
    leadId: number,
    data: { subject: string; body_html: string },
  ): Promise<Communication> {
    const response = await api.post(`/leads/${leadId}/communications/email`, data);
    return response.data;
  },

  async getUnmatched(
    filters: { page?: number; page_size?: number } = {},
  ): Promise<CommunicationListResponse> {
    const params = new URLSearchParams();
    if (filters.page) params.append('page', String(filters.page));
    if (filters.page_size) params.append('page_size', String(filters.page_size));
    const response = await api.get(`/communications/unmatched?${params.toString()}`);
    return response.data;
  },

  async assignToLead(communicationId: number, leadId: number): Promise<Communication> {
    const response = await api.post(`/communications/${communicationId}/assign`, {
      lead_id: leadId,
    });
    return response.data;
  },

  async assignToPatient(communicationId: number, patientId: number): Promise<Communication> {
    const response = await api.post(`/communications/${communicationId}/assign`, {
      patient_id: patientId,
    });
    return response.data;
  },
};
